/**
 * TWO-FACTOR AUTHENTICATION MODAL
 * 
 * Modal que se muestra durante el login cuando el usuario tiene MFA habilitado.
 * Usa Firebase MFA nativo para verificación.
 * 
 * FLUJO:
 * 1. Usuario intenta login → Firebase detecta MFA → Capturamos el resolver
 * 2. Mostramos opciones de verificación (teléfonos enrollados)
 * 3. Enviamos SMS y pedimos código
 * 4. Verificamos código → Login completo
 * 5. Opcionalmente, confiamos el dispositivo (30 días)
 */

import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { mfaService, MFALoginResolver } from '@/core/security/mfa.service';
import { trustedDeviceService } from '@/core/security/trusted-device.service';
import { Logger } from '@/core/utils';

const logger = Logger.getLogger('TwoFactorModal');

interface TwoFactorModalProps {
  isOpen: boolean;
  resolver: MFALoginResolver | null;
  onComplete: (success: boolean) => void;
  onLogout: () => void;
}

type Step = 
  | 'select_factor' // Seleccionar factor (si hay más de uno)
  | 'confirm_send'  // Confirmar envío de SMS
  | 'sending'       // Enviando SMS
  | 'code_input'    // Ingresando código
  | 'verifying'     // Verificando código
  | 'trust_device'  // Preguntar si confiar
  | 'trusting'      // Guardando confianza
  | 'error';        // Error

export const TwoFactorModal: React.FC<TwoFactorModalProps> = ({
  isOpen,
  resolver,
  onComplete,
  onLogout
}) => {
  const [step, setStep] = useState<Step>('confirm_send');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(0);
  const [selectedFactor, setSelectedFactor] = useState<string | null>(null);
  const recaptchaRef = useRef<HTMLDivElement>(null);
  const codeInputRef = useRef<HTMLInputElement>(null);

  // Información del dispositivo
  const deviceInfo = trustedDeviceService.getDeviceInfo();

  // Obtener teléfono enmascarado del factor seleccionado
  const selectedHint = resolver?.hints.find(h => h.uid === selectedFactor) || resolver?.hints[0];
  const maskedPhone = selectedHint?.phoneNumber
    ? selectedHint.phoneNumber.substring(0, 6) + '***' + selectedHint.phoneNumber.slice(-2)
    : '';

  // Inicializar cuando se abre
  useEffect(() => {
    if (isOpen && resolver) {
      // Si hay más de un factor, mostrar selector
      if (resolver.hints.length > 1) {
        setStep('select_factor');
      } else if (resolver.hints.length === 1) {
        setSelectedFactor(resolver.hints[0].uid);
        setStep('confirm_send');
      } else {
        setStep('error');
        setError('No hay factores de autenticación configurados');
      }
      setCode('');
      setError(null);
    }
  }, [isOpen, resolver]);

  // Inicializar reCAPTCHA cuando es necesario
  useEffect(() => {
    if (isOpen && (step === 'confirm_send' || step === 'select_factor') && recaptchaRef.current) {
      mfaService.initializeRecaptcha('mfa-recaptcha').catch(err => {
        logger.error('Failed to init reCAPTCHA', err);
      });
    }

    return () => {
      mfaService.cleanup();
    };
  }, [isOpen, step]);

  // Focus en input de código
  useEffect(() => {
    if (step === 'code_input' && codeInputRef.current) {
      setTimeout(() => codeInputRef.current?.focus(), 100);
    }
  }, [step]);

  // Countdown para reenvío
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Enviar SMS
  const handleSendCode = async () => {
    setStep('sending');
    setError(null);

    const result = await mfaService.sendLoginVerification(selectedFactor || undefined);

    if (result.success) {
      setStep('code_input');
      setCountdown(60);
    } else {
      setStep('error');
      setError(result.error || 'Error al enviar SMS');
    }
  };

  // Verificar código
  const handleVerifyCode = async () => {
    if (code.length !== 6) {
      setError('El código debe tener 6 dígitos');
      return;
    }

    setStep('verifying');
    setError(null);

    const result = await mfaService.completeLogin(code);

    if (result.success) {
      // Verificación exitosa → preguntar por dispositivo de confianza
      setStep('trust_device');
    } else {
      setStep('code_input');
      setError(result.error || 'Código incorrecto');
      setCode('');
    }
  };

  // Reenviar código
  const handleResend = async () => {
    if (countdown > 0) return;

    const result = await mfaService.sendLoginVerification(selectedFactor || undefined);
    
    if (result.success) {
      setCountdown(60);
      setCode('');
      setError(null);
    } else {
      setError(result.error || 'Error al reenviar');
    }
  };

  // Confiar en dispositivo
  const handleTrustDevice = async (trust: boolean) => {
    if (trust) {
      setStep('trusting');
      await trustedDeviceService.trustDevice();
    }
    
    onComplete(true);
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-gradient-to-br from-stone-900/90 to-stone-900/95 backdrop-blur-md">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-300">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-5">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
              <span className="text-3xl">🔐</span>
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Verificación de Seguridad</h2>
              <p className="text-sm text-white/80">Protección de datos de pacientes</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          
          {/* PASO: Seleccionar factor */}
          {step === 'select_factor' && resolver && (
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-4 bg-emerald-100 rounded-full flex items-center justify-center">
                <span className="text-4xl">📱</span>
              </div>
              
              <h3 className="text-lg font-semibold text-stone-800 mb-4">
                Selecciona método de verificación
              </h3>

              <div className="space-y-3 mb-6">
                {resolver.hints.map((hint) => (
                  <button
                    key={hint.uid}
                    onClick={() => {
                      setSelectedFactor(hint.uid);
                      setStep('confirm_send');
                    }}
                    className="w-full p-4 bg-stone-50 border-2 border-stone-200 rounded-xl hover:border-emerald-500 hover:bg-emerald-50 transition-all flex items-center gap-4"
                  >
                    <span className="text-2xl">📲</span>
                    <div className="text-left">
                      <p className="font-medium text-stone-800">{hint.displayName}</p>
                      <p className="text-sm text-stone-500">{hint.phoneNumber}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* PASO: Confirmar envío */}
          {step === 'confirm_send' && (
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-4 bg-emerald-100 rounded-full flex items-center justify-center">
                <span className="text-4xl">📲</span>
              </div>
              
              <p className="text-stone-600 mb-2">
                Enviaremos un código de verificación a:
              </p>
              
              <p className="text-xl font-bold text-stone-800 mb-6">
                {maskedPhone}
              </p>

              <button
                onClick={handleSendCode}
                className="w-full py-4 bg-emerald-600 text-white font-semibold rounded-xl hover:bg-emerald-700 transition-colors text-lg"
              >
                📤 Enviar código SMS
              </button>

              <p className="mt-4 text-xs text-stone-500">
                Este paso verifica que eres tú quien accede a los datos sensibles
              </p>
            </div>
          )}

          {/* PASO: Enviando */}
          {step === 'sending' && (
            <div className="text-center py-8">
              <div className="w-16 h-16 mx-auto mb-4 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin"></div>
              <p className="text-stone-600 font-medium">Enviando código SMS...</p>
              <p className="text-sm text-stone-500 mt-2">Esto puede tardar unos segundos</p>
            </div>
          )}

          {/* PASO: Ingresar código */}
          {step === 'code_input' && (
            <div>
              <div className="text-center mb-6">
                <p className="text-stone-600 mb-1">
                  Ingresa el código de 6 dígitos enviado a:
                </p>
                <p className="font-semibold text-stone-800">{maskedPhone}</p>
              </div>

              <div className="mb-4">
                <input
                  ref={codeInputRef}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  className="w-full px-4 py-4 border-2 border-stone-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-3xl text-center tracking-[0.5em] font-mono transition-all"
                  autoComplete="one-time-code"
                />
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg animate-in fade-in">
                  <p className="text-sm text-red-700 flex items-center gap-2">
                    <span>⚠️</span> {error}
                  </p>
                </div>
              )}

              <button
                onClick={handleVerifyCode}
                disabled={code.length !== 6}
                className="w-full py-4 bg-emerald-600 text-white font-semibold rounded-xl hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-lg"
              >
                ✓ Verificar código
              </button>

              <div className="mt-4 text-center">
                {countdown > 0 ? (
                  <p className="text-sm text-stone-500">
                    Reenviar en <span className="font-mono font-bold">{countdown}</span> segundos
                  </p>
                ) : (
                  <button
                    onClick={handleResend}
                    className="text-sm text-emerald-600 hover:text-emerald-700 font-medium"
                  >
                    📤 Reenviar código
                  </button>
                )}
              </div>
            </div>
          )}

          {/* PASO: Verificando */}
          {step === 'verifying' && (
            <div className="text-center py-8">
              <div className="w-16 h-16 mx-auto mb-4 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin"></div>
              <p className="text-stone-600 font-medium">Verificando código...</p>
            </div>
          )}

          {/* PASO: Confiar en dispositivo */}
          {step === 'trust_device' && (
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-4 bg-emerald-100 rounded-full flex items-center justify-center">
                <span className="text-4xl">✅</span>
              </div>
              
              <h3 className="text-lg font-semibold text-stone-800 mb-2">
                ¡Verificación exitosa!
              </h3>
              
              <p className="text-stone-600 mb-6">
                ¿Quieres recordar este dispositivo para no pedir verificación cada vez?
              </p>

              <div className="p-4 bg-stone-50 border border-stone-200 rounded-xl mb-6">
                <div className="flex items-center gap-3 text-left">
                  <span className="text-2xl">💻</span>
                  <div>
                    <p className="font-medium text-stone-800">{deviceInfo.name}</p>
                    <p className="text-xs text-stone-500">Confianza válida por 30 días</p>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => handleTrustDevice(false)}
                  className="flex-1 py-3 bg-stone-100 text-stone-700 font-medium rounded-xl hover:bg-stone-200 transition-colors"
                >
                  No, pedir siempre
                </button>
                <button
                  onClick={() => handleTrustDevice(true)}
                  className="flex-1 py-3 bg-emerald-600 text-white font-semibold rounded-xl hover:bg-emerald-700 transition-colors"
                >
                  ✓ Confiar
                </button>
              </div>
            </div>
          )}

          {/* PASO: Guardando confianza */}
          {step === 'trusting' && (
            <div className="text-center py-8">
              <div className="w-16 h-16 mx-auto mb-4 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin"></div>
              <p className="text-stone-600 font-medium">Guardando preferencia...</p>
            </div>
          )}

          {/* PASO: Error */}
          {step === 'error' && (
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
                <span className="text-4xl">❌</span>
              </div>
              
              <h3 className="text-lg font-semibold text-stone-800 mb-2">
                Error de verificación
              </h3>
              
              <p className="text-red-600 mb-6">{error}</p>

              <div className="flex gap-3">
                <button
                  onClick={onLogout}
                  className="flex-1 py-3 bg-stone-100 text-stone-700 font-medium rounded-xl hover:bg-stone-200 transition-colors"
                >
                  Salir
                </button>
                <button
                  onClick={() => setStep('confirm_send')}
                  className="flex-1 py-3 bg-emerald-600 text-white font-semibold rounded-xl hover:bg-emerald-700 transition-colors"
                >
                  Reintentar
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-stone-50 border-t border-stone-200">
          <div className="flex items-center justify-between">
            <p className="text-xs text-stone-500 flex items-center gap-1">
              🛡️ Verificación de dos factores (2FA)
            </p>
            {step !== 'sending' && step !== 'verifying' && step !== 'trusting' && step !== 'trust_device' && (
              <button
                onClick={onLogout}
                className="text-sm text-stone-500 hover:text-red-600 transition-colors"
              >
                Cerrar sesión
              </button>
            )}
          </div>
        </div>

        {/* reCAPTCHA container (invisible) */}
        <div id="mfa-recaptcha" ref={recaptchaRef}></div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default TwoFactorModal;
